import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { formatDateTime, formatRelativeTime } from "@/utils/dateUtils";

const AUTH_LOG_EVENTS = [
  "LOGIN",
  "LOGOUT",
  "TOKEN_REFRESH",
  "IMPERSONATION_START",
  "PASSWORD_RESET",
  "USER_ENABLED",
  "USER_DISABLED",
  "USER_CREATED",
  "USER_DELETED",
  "ROLE_CHANGED",
] as const;

type AuthLogEvent = (typeof AUTH_LOG_EVENTS)[number];
type AuthLogOutcome = "SUCCESS" | "FAILURE";
type AuthLogMethod =
  | "PASSWORD"
  | "PIN"
  | "OAUTH"
  | "MAGIC_LINK"
  | "TOKEN_EXCHANGE"
  | "REFRESH";

interface AuthUserDisplay {
  label: string;
  role: string;
}

interface AuthLogEntry {
  id: string;
  event: AuthLogEvent;
  outcome: AuthLogOutcome;
  method?: AuthLogMethod;
  subjectUserId?: string;
  actorUserId?: string;
  subjectIdentifier?: string;
  ipAddress?: string;
  reason?: string;
  createdAt: string;
  actor?: AuthUserDisplay;
  subject?: AuthUserDisplay;
}

interface FilterState {
  event: string;
  outcome: string;
}

const EMPTY_FILTERS: FilterState = { event: "", outcome: "" };

export const UsersMonitoringPage: React.FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  // Placeholder until backend integration — replace with useAuthLogs(query)
  const entries: AuthLogEntry[] = [];
  const isLoading = false;
  const isError = false;
  const totalPages = 1;
  const pageNumbers = [1];

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const hasActiveFilters = filters.event !== "" || filters.outcome !== "";

  return (
    <div className="flex flex-col gap-6 p-12 max-w-[1344px] mx-auto">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Shield className="size-6 text-sidebar-foreground" />
          <h1 className="text-2xl font-semibold">
            {t("monitoring.usersMonitoring.title")}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("monitoring.usersMonitoring.description")}
        </p>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={filters.event || "all"}
          onValueChange={(v) =>
            handleFilterChange("event", v === "all" ? "" : v)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={t("monitoring.usersMonitoring.filters.allEvents")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("monitoring.usersMonitoring.filters.allEvents")}
            </SelectItem>
            {AUTH_LOG_EVENTS.map((ev) => (
              <SelectItem key={ev} value={ev}>
                {t(`monitoring.usersMonitoring.events.${ev}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.outcome || "all"}
          onValueChange={(v) =>
            handleFilterChange("outcome", v === "all" ? "" : v)
          }
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue
              placeholder={t("monitoring.usersMonitoring.filters.allOutcomes")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("monitoring.usersMonitoring.filters.allOutcomes")}
            </SelectItem>
            <SelectItem value="SUCCESS">
              {t("monitoring.usersMonitoring.outcomes.SUCCESS")}
            </SelectItem>
            <SelectItem value="FAILURE">
              {t("monitoring.usersMonitoring.outcomes.FAILURE")}
            </SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="new_ghost" size="sm" onClick={resetFilters}>
            {t("monitoring.usersMonitoring.filters.reset")}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-base-border">
        <Table>
          <TableHeader>
            <TableRow className="h-10 bg-muted/40 hover:bg-muted/40">
              <TableHead className="pl-4">
                {t("monitoring.usersMonitoring.columns.timestamp")}
              </TableHead>
              <TableHead>
                {t("monitoring.usersMonitoring.columns.event")}
              </TableHead>
              <TableHead>
                {t("monitoring.usersMonitoring.columns.outcome")}
              </TableHead>
              <TableHead>
                {t("monitoring.usersMonitoring.columns.method")}
              </TableHead>
              <TableHead>
                {t("monitoring.usersMonitoring.columns.actor")}
              </TableHead>
              <TableHead>
                {t("monitoring.usersMonitoring.columns.subject")}
              </TableHead>
              <TableHead className="pr-4">
                {t("monitoring.usersMonitoring.columns.ipAddress")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  {t("monitoring.usersMonitoring.loading")}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && isError && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-sm text-destructive"
                >
                  {t("monitoring.usersMonitoring.error")}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && entries.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  {t("monitoring.usersMonitoring.empty")}
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !isError &&
              entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="pl-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="text-sm text-foreground">
                        {formatDateTime(entry.createdAt)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatRelativeTime(entry.createdAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <AuthEventBadge event={entry.event} />
                  </TableCell>
                  <TableCell>
                    <AuthOutcomeBadge outcome={entry.outcome} />
                  </TableCell>
                  <TableCell>
                    <MethodCell method={entry.method} />
                  </TableCell>
                  <TableCell>
                    <UserCell
                      display={entry.actor}
                      fallbackId={entry.actorUserId}
                    />
                  </TableCell>
                  <TableCell>
                    <UserCell
                      display={entry.subject}
                      fallbackId={entry.subjectIdentifier}
                    />
                  </TableCell>
                  <TableCell className="pr-4">
                    <IpCell ip={entry.ipAddress} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                className={cn(
                  "cursor-pointer",
                  page === 1 && "pointer-events-none opacity-50",
                )}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              />
            </PaginationItem>
            {pageNumbers.map((p) => (
              <PaginationItem key={p}>
                <PaginationLink
                  isActive={page === p}
                  onClick={() => setPage(p)}
                  className="cursor-pointer"
                >
                  {p}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                className={cn(
                  "cursor-pointer",
                  page >= totalPages && "pointer-events-none opacity-50",
                )}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

interface MethodCellProps {
  method?: AuthLogMethod;
}

const MethodCell: React.FC<MethodCellProps> = ({ method }) => {
  const { t } = useTranslation();
  if (!method) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-xs font-mono text-muted-foreground">
      {t(`monitoring.usersMonitoring.methods.${method}`)}
    </span>
  );
};

interface UserCellProps {
  display?: AuthUserDisplay;
  fallbackId?: string;
}

const UserCell: React.FC<UserCellProps> = ({ display, fallbackId }) => {
  if (display) {
    return (
      <div className="flex flex-col">
        <span className="text-sm text-foreground">{display.role}</span>
        <span className="text-xs font-mono text-muted-foreground">
          {display.label}
        </span>
      </div>
    );
  }
  if (fallbackId) {
    return (
      <span className="text-xs font-mono text-muted-foreground">
        {fallbackId}
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
};

interface IpCellProps {
  ip?: string;
}

const IpCell: React.FC<IpCellProps> = ({ ip }) => {
  if (!ip) return <span className="text-muted-foreground">—</span>;
  return <span className="text-xs font-mono text-muted-foreground">{ip}</span>;
};

const EVENT_BADGE_VARIANT: Record<
  AuthLogEvent,
  React.ComponentProps<typeof Badge>["variant"]
> = {
  LOGIN: "new_secondary",
  LOGOUT: "new_secondary",
  TOKEN_REFRESH: "new_secondary",
  IMPERSONATION_START: "status_new",
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  PASSWORD_RESET: "status_new",
  USER_ENABLED: "success",
  USER_DISABLED: "new_destructive",
  USER_CREATED: "success",
  USER_DELETED: "new_destructive",
  ROLE_CHANGED: "status_in_progress",
};

interface AuthEventBadgeProps {
  event: AuthLogEvent;
}

const AuthEventBadge: React.FC<AuthEventBadgeProps> = ({ event }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={EVENT_BADGE_VARIANT[event]} className="whitespace-nowrap">
      {t(`monitoring.usersMonitoring.events.${event}`)}
    </Badge>
  );
};

interface AuthOutcomeBadgeProps {
  outcome: AuthLogOutcome;
}

const AuthOutcomeBadge: React.FC<AuthOutcomeBadgeProps> = ({ outcome }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={outcome === "SUCCESS" ? "success" : "new_destructive"}>
      {t(`monitoring.usersMonitoring.outcomes.${outcome}`)}
    </Badge>
  );
};
